import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Spinner } from "../../atoms/Spinner";
import { ModalShell } from "../../atoms/ModalShell";
import { CustomButton } from "../../atoms/CustomButton";
import { StatusBadge } from "../../atoms/StatusBadge";
import HttpService from "../../../services/http";

/**
 * Pick the MODEL and the UPSTREAM for one chat configuration.
 *
 * Every price shown here comes from OpenRouter as-is — we only convert per-token prices to the
 * per-million unit humans compare in, and sort. Nothing is estimated.
 *
 * The upstream choice is the part that is easy to get wrong: OpenRouter serves the same model from
 * several providers whose price can differ by more than 10x, whose uptime ranges from ~45% to 100%,
 * and — the one that bites silently — whose CONTEXT WINDOW can be a fraction of the model's, which
 * truncates a long RAG prompt without any error. Leaving the choice unpinned hands it to OpenRouter's
 * balancer, which optimises for availability, not for our bill.
 */

type OrModel = {
  model_name: string;
  name: string;
  context_length: number | null;
  max_completion_tokens: number | null;
  price_in_per_1m: number | null;
  price_out_per_1m: number | null;
};

type OrEndpoint = {
  provider_name: string;
  /**
   * The precise endpoint id (`google-ai-studio/flex`). Pinning uses THIS, not the provider name:
   * one provider often serves the same model from several endpoints at different prices — pinning
   * "Google AI Studio" leaves OpenRouter free to pick the $0.05 one or the $0.18 one.
   */
  tag: string | null;
  quantization: string | null;
  context_length: number | null;
  price_in_per_1m: number | null;
  price_out_per_1m: number | null;
  discount: number;
  uptime_last_30m: number | null;
  latency_p50_ms: number | null;
};

type Props = {
  base: string;
  /** null closes the modal; otherwise the configuration being edited. */
  scenario: string | null;
  scenarioLabel: string;
  currentModel?: string;
  currentUpstream?: string | null;
  onClose: () => void;
  onSaved: () => void;
};

const money = (v: number | null | undefined) => (v == null ? "—" : `$${v}`);
const pct = (v: number | null | undefined) =>
  v == null ? "—" : `${Number(v).toFixed(1)}%`;
const compact = (v: number | null | undefined) =>
  v == null ? "—" : v >= 1000 ? `${Math.round(v / 1000)}k` : String(v);

/** Uptime is a routing risk, not a detail: below ~99% a pinned upstream will drop calls. */
const uptimeTone = (v: number | null) =>
  v == null ? "text-gray-400" : v >= 99 ? "text-gray-500" : "text-amber-600";

const ChatUpstreamPicker: React.FC<Props> = ({
  base,
  scenario,
  scenarioLabel,
  currentModel,
  currentUpstream,
  onClose,
  onSaved,
}) => {
  const [query, setQuery] = useState("");
  const [models, setModels] = useState<OrModel[]>([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [model, setModel] = useState<string | undefined>(currentModel);
  const [endpoints, setEndpoints] = useState<OrEndpoint[] | null>(null);
  const [endpointsLoading, setEndpointsLoading] = useState(false);
  const [upstream, setUpstream] = useState<string | null>(
    currentUpstream ?? null
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!scenario) return;
    setQuery("");
    setModel(currentModel);
    setUpstream(currentUpstream ?? null);
    setEndpoints(null);
  }, [scenario, currentModel, currentUpstream]);

  useEffect(() => {
    if (!scenario) return;
    let alive = true;
    const t = setTimeout(async () => {
      setModelsLoading(true);
      try {
        const res = await HttpService.get<{ element: { models: OrModel[] } }>(
          `${base}/openrouter/models?limit=40${
            query ? `&q=${encodeURIComponent(query)}` : ""
          }`
        );
        if (alive) setModels(res.data?.element?.models ?? []);
      } catch {
        if (alive) setModels([]);
      } finally {
        if (alive) setModelsLoading(false);
      }
    }, 300);
    return () => {
      alive = false;
      clearTimeout(t);
    };
  }, [base, scenario, query]);

  const loadEndpoints = useCallback(
    async (modelName: string) => {
      setEndpointsLoading(true);
      setEndpoints(null);
      try {
        const res = await HttpService.get<{
          element: { endpoints: OrEndpoint[] };
        }>(`${base}/openrouter/models/${modelName}/endpoints`);
        setEndpoints(res.data?.element?.endpoints ?? []);
      } catch {
        setEndpoints([]);
      } finally {
        setEndpointsLoading(false);
      }
    },
    [base]
  );

  useEffect(() => {
    if (scenario && model) loadEndpoints(model);
  }, [scenario, model, loadEndpoints]);

  // The model's full context window: an upstream offering less will silently truncate long prompts.
  const fullContext = useMemo(() => {
    if (!endpoints || !endpoints.length) return null;
    return Math.max(...endpoints.map((e) => e.context_length ?? 0));
  }, [endpoints]);

  const cheapest = useMemo(() => {
    if (!endpoints || !endpoints.length) return null;
    return endpoints.reduce((a, b) =>
      (a.price_in_per_1m ?? Infinity) <= (b.price_in_per_1m ?? Infinity) ? a : b
    );
  }, [endpoints]);

  const save = async () => {
    if (!scenario || !model) return;
    setSaving(true);
    try {
      await HttpService.put(`${base}/chat/${scenario}`, {
        model_name: model,
        provider_routing: upstream
          ? {
              order: [upstream],
              allow_fallbacks: true,
              data_collection: "deny",
            }
          : { allow_fallbacks: true, data_collection: "deny" },
      });
      toast.success(
        `${scenarioLabel}: ${model}${upstream ? ` via ${upstream}` : ""}`
      );
      onSaved();
      onClose();
    } catch (err: any) {
      toast.error(
        err?.response?.data?.message || "Could not save the configuration"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalShell
      show={Boolean(scenario)}
      onClose={onClose}
      size="5xl"
      title={`${scenarioLabel}: model and endpoint`}
      subtitle="Prices come straight from OpenRouter, per 1M tokens. Nothing is estimated. Pinning a precise endpoint is what makes the price predictable."
      footer={
        <>
          <CustomButton variant="secondary" title="Cancel" onClick={onClose} />
          <CustomButton
            variant="primary"
            title="Save"
            onClick={save}
            isLoading={saving}
            disabled={!model}
          />
        </>
      }
    >
      <div className="grid gap-5 lg:grid-cols-[minmax(0,22rem)_minmax(0,1fr)]">
        {/* ── 1. the model ─────────────────────────────────────────────── */}
        <div className="min-w-0">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            1. Model
          </div>
          <input
            type="text"
            placeholder="Search OpenRouter (e.g. gemini, claude, deepseek)"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm"
          />
          <div className="mt-2 max-h-80 overflow-y-auto rounded-lg border border-gray-200">
            {modelsLoading ? (
              <div className="flex justify-center py-6">
                <Spinner size="sm" />
              </div>
            ) : models.length === 0 ? (
              <p className="p-3 text-xs text-gray-500">No model matches.</p>
            ) : (
              <ul className="divide-y divide-gray-100">
                {models.map((m) => {
                  const selected = m.model_name === model;
                  return (
                    <li key={m.model_name}>
                      <button
                        type="button"
                        onClick={() => {
                          setModel(m.model_name);
                          setUpstream(null); // upstreams differ per model
                        }}
                        className={`w-full px-3 py-2 text-left transition-colors ${
                          selected ? "bg-blue-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="truncate text-xs font-medium text-gray-900">
                          {m.model_name}
                        </div>
                        <div className="mt-0.5 flex items-center gap-2 text-xs text-gray-500">
                          <span className="tabular-nums">
                            {money(m.price_in_per_1m)} /{" "}
                            {money(m.price_out_per_1m)}
                          </span>
                          <span>· {compact(m.context_length)} ctx</span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <p className="mt-1 text-xs text-gray-400">
            Listed price. The endpoint you pin on the right is often cheaper.
          </p>
        </div>

        {/* ── 2. the upstream ──────────────────────────────────────────── */}
        <div className="min-w-0">
          <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
            2. Endpoint{" "}
            {model ? (
              <span className="normal-case text-gray-400">({model})</span>
            ) : null}
          </div>

          {!model ? (
            <p className="rounded-lg border border-dashed border-gray-300 p-4 text-xs text-gray-500">
              Pick a model first.
            </p>
          ) : endpointsLoading ? (
            <div className="flex justify-center py-10">
              <Spinner size="md" />
            </div>
          ) : !endpoints || endpoints.length === 0 ? (
            <p className="rounded-lg border border-dashed border-gray-300 p-4 text-xs text-gray-500">
              OpenRouter reports no endpoint for this model.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-lg border border-gray-200">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50 text-left text-xs text-gray-500">
                    <th className="px-3 py-2 font-medium">Endpoint</th>
                    <th className="px-3 py-2 text-right font-medium">
                      in / out
                    </th>
                    <th className="px-3 py-2 text-right font-medium">
                      Context
                    </th>
                    <th className="px-3 py-2 text-right font-medium">Uptime</th>
                    <th className="px-3 py-2 text-right font-medium">
                      Latency
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  <tr
                    className={`cursor-pointer transition-colors ${
                      upstream === null ? "bg-blue-50" : "hover:bg-gray-50"
                    }`}
                    onClick={() => setUpstream(null)}
                  >
                    <td className="px-3 py-2" colSpan={5}>
                      <div className="flex items-center gap-2">
                        <input
                          type="radio"
                          readOnly
                          checked={upstream === null}
                        />
                        <span className="text-xs text-gray-700">
                          No pin, let OpenRouter choose
                        </span>
                        <span className="text-xs text-amber-600">
                          price not guaranteed
                        </span>
                      </div>
                    </td>
                  </tr>
                  {endpoints.map((e, i) => {
                    const pinValue = e.tag ?? e.provider_name;
                    const selected = upstream === pinValue;
                    const truncates =
                      fullContext != null &&
                      (e.context_length ?? 0) < fullContext;
                    return (
                      <tr
                        key={`${e.provider_name}-${i}`}
                        onClick={() => setUpstream(pinValue)}
                        className={`cursor-pointer transition-colors ${
                          selected ? "bg-blue-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap items-center gap-1.5">
                            <input type="radio" readOnly checked={selected} />
                            <span className="text-xs font-medium text-gray-900">
                              {e.provider_name}
                            </span>
                            {e.tag && e.tag !== e.provider_name && (
                              <span className="font-mono text-xs text-gray-400">
                                {e.tag}
                              </span>
                            )}
                            {cheapest && e === cheapest && (
                              <StatusBadge color="success" size="xs">
                                cheapest
                              </StatusBadge>
                            )}
                            {e.discount > 0 && (
                              <StatusBadge
                                color="warning"
                                size="xs"
                                // A promotional price reverts when the deal ends.
                                className="cursor-help"
                              >
                                −{Math.round(e.discount * 100)}% promo
                              </StatusBadge>
                            )}
                            {e.quantization && e.quantization !== "unknown" && (
                              <span className="text-xs text-gray-400">
                                {e.quantization}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-700">
                          {money(e.price_in_per_1m)} /{" "}
                          {money(e.price_out_per_1m)}
                        </td>
                        <td
                          className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${
                            truncates ? "text-amber-600" : "text-gray-500"
                          }`}
                          title={
                            truncates
                              ? "Smaller than this model's full window: a long prompt would be truncated"
                              : undefined
                          }
                        >
                          {compact(e.context_length)}
                        </td>
                        <td
                          className={`whitespace-nowrap px-3 py-2 text-right tabular-nums ${uptimeTone(
                            e.uptime_last_30m
                          )}`}
                        >
                          {pct(e.uptime_last_30m)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums text-gray-500">
                          {e.latency_p50_ms != null
                            ? `${e.latency_p50_ms} ms`
                            : "—"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <p className="mt-2 text-xs text-gray-400">
            A pin stays a preference: if that endpoint is down, OpenRouter falls
            through to another one, and prompts are never sent to a provider
            that retains them. Rows sharing a provider name are distinct
            endpoints, and pinning the exact one is what fixes the price.
          </p>
        </div>
      </div>
    </ModalShell>
  );
};

export default ChatUpstreamPicker;
